import type { LegacyReport } from '@mathflat/domain/Report'
import type { Revision } from '@mathflat/domain/SchoolSystem'
import { format } from 'date-fns'
import { action, makeObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { EMPTY_NUMBER_ID, EMPTY_STRING_ID } from 'src/constant/common'
import type { DateStr } from 'src/types'

import dateUtils from '@/utils/DateUtils'

import { DTO } from '../DTO'

// 편집 화면이 쓰는 필드만 든다. 학생 이름·PDF 상태·생성 시각은 목록(SimpleReport)의 몫이다
export type ReportData = Omit<
  LegacyReport,
  | 'startDate'
  | 'endDate'
  | 'worksheetTagFlags'
  | 'selfLearningFlag'
  | 'studentName'
  | 'status'
  | 'createDatetime'
> & {
  startDate: DateStr<'-'>
  endDate: DateStr<'-'>
  learningHistory: {
    worksheetTagFlags: LegacyReport['worksheetTagFlags']
    selfLearningFlag: LegacyReport['selfLearningFlag']
    studentWorksheetIds?: number[]
    studentWorkbookIds?: number[]
  }
}

export class Report extends DTO implements ReportData {
  static OLD_COMPONENT_LIST = [
    { label: '주별 분석', value: 'WEEKLY' },
    { label: '단원별 분석', value: 'CHAPTER_SUMMARY' },
    { label: '난이도별 분석', value: 'LEVEL' },
    { label: '단원별 세부 페이지', value: 'CHAPTER_DETAIL' },
    { label: '학습 내역', value: 'STUDY_HISTORY' },
    {
      label: '영역별 분석 (내용 영역·행동 영역)',
      value: 'ASSESSMENT',
    },
  ] as const

  id = EMPTY_NUMBER_ID
  studentId = EMPTY_STRING_ID
  comment = EMPTY_STRING_ID
  pdfUrl = EMPTY_STRING_ID
  chapter = EMPTY_STRING_ID
  areaScore = EMPTY_NUMBER_ID
  behaviorScore = EMPTY_NUMBER_ID
  totalScore = EMPTY_NUMBER_ID
  totalTier = EMPTY_NUMBER_ID
  type: LegacyReport['type'] = 'MONTHLY'
  yearMonth = dateUtils.toYearMonth(new Date(), '-')
  startDate = dateUtils.toDateString(new Date(), '-')
  endDate = dateUtils.toDateString(new Date(), '-')
  title = EMPTY_STRING_ID
  extraScore = EMPTY_NUMBER_ID
  learningHistory: ReportData['learningHistory'] = {
    worksheetTagFlags: [],
    selfLearningFlag: false,
    studentWorksheetIds: [],
    studentWorkbookIds: [],
  }
  revision: Revision = 'CURRICULUM_15'

  componentFlags: ReportData['componentFlags'] = []
  opened = false
  deleted = false
  sending = false

  constructor(data: Partial<LegacyReport>) {
    super()

    makeObservable(this, {
      id: observable,
      studentId: observable,
      comment: observable,
      pdfUrl: observable,
      chapter: observable,
      areaScore: observable,
      behaviorScore: observable,
      totalScore: observable,
      totalTier: observable,
      type: observable,
      yearMonth: observable,
      startDate: observable,
      endDate: observable,
      title: observable,
      extraScore: observable,
      learningHistory: observable,
      componentFlags: observable,
      opened: observable,
      sending: observable,
      deleted: observable,
      revision: observable,
      reportRangeDate: action,
    })

    this.update<ReportData>({
      ...data,
      // 서버 LocalDate 는 yyyy-MM-dd 로 오고 스키마가 경계에서 검증한다
      startDate: data.startDate as DateStr<'-'> | undefined,
      endDate: data.endDate as DateStr<'-'> | undefined,
      learningHistory: {
        worksheetTagFlags: data.worksheetTagFlags ?? [],
        selfLearningFlag: data.selfLearningFlag ?? false,
        studentWorksheetIds: [],
        studentWorkbookIds: [],
      },
    })
  }

  reportRangeDate = computedFn((yearOpt: 'yy' | 'yyyy') => {
    if (this.type === 'CUSTOM_PERIOD') {
      const startDate = format(new Date(this.startDate), `${yearOpt}.MM.dd`)
      const endDate = format(new Date(this.endDate), `${yearOpt}.MM.dd`)
      return `${startDate}~${endDate}`
    }
    return format(new Date(this.yearMonth), `${yearOpt}년 MM월`)
  })
}
